import React from 'react';
import Link from 'next/link';
import omit from 'lodash/omit';
import Button from '@mui/material/Button';
import GeneralMenu from '@/components/navigation/GeneralMenu';
import GeneralChart from '@/components/seo/GeneralChart';
import SvgElectric from '../SvgElectric';
import { AuthContext, JobTitleContext } from '@/context/create-context';
import {
  CANDIDATES_ME_ROUTER_PATH,
  DEFAULT_CANDIDATES_ME_ROUTER_PATH,
  RESUME_EVALUATION_ROUTER_PATH,
} from '@/constants/router-path';
import { RESUME_FOCUS_ITEMS } from '@/constants/job-title';
import { sortByOrder } from '@/utils/common-utils';

const STAGE_ORDER = ['0-1', '1-3', '3-5', '5-10'];

const ExperienceSwitcher = ({
  data,
}: {
  data: Record<string, Record<string, string>>;
}) => {
  const stages = Object.keys(data).sort(sortByOrder(STAGE_ORDER));
  const [activeStage, setActiveStage] = React.useState(stages[0]);

  return (
    <>
      <div className="flex gap-10 mb-4">
        {stages.map((stage, index) => (
          <span
            key={index}
            onClick={() => setActiveStage(stage)}
            className={`transition-colors cursor-pointer font-semibold ${
              activeStage === stage
                ? 'border-b border-primary border-solid'
                : 'text-web-grey-600 hover:text-primary'
            }`}
          >
            {stage}
          </span>
        ))}
      </div>
      {stages.map((stage) => (
        <ul
          className={`list-inside !ml-2 ${activeStage === stage ? 'max-h-full' : 'max-h-0 overflow-hidden'}`}
          key={stage}
        >
          {Object.entries(data[stage])
            .sort(([k1], [k2]) => {
              if (k1.includes('示例') && !k2.includes('示例')) return 1;
              if (!k1.includes('示例') && k2.includes('示例')) return -1;
              return 0;
            })
            .map(([key, value]) => (
              <li key={key}>{`${key}：${value}`}</li>
            ))}
        </ul>
      ))}
    </>
  );
};

const ResumeFocus = (props: {
  show: boolean;
  abilityData: Record<string, any>;
  achievementData: Record<string, any>;
  resumeScreeningData: Record<string, any>;
  standoutData: Record<string, any>;
  highlightData: Record<string, any>;
  marketPreferenceData: Record<string, any>;
  avoidTrapsData: Record<string, any>;
}) => {
  const {
    show,
    abilityData,
    achievementData,
    resumeScreeningData,
    standoutData,
    highlightData,
    marketPreferenceData,
    avoidTrapsData,
  } = props;
  const { displayName } = React.useContext(JobTitleContext);
  const { isLogin } = React.useContext(AuthContext);

  return (
    <div
      className={`relative ${show ? 'max-h-full' : 'max-h-0 overflow-hidden'}`}
    >
      <GeneralMenu menuList={RESUME_FOCUS_ITEMS} />
      <h2 className="text-xl font-semibold">
        作为求职者，如何构建匹配职位能力的简历
      </h2>
      <GeneralChart
        id={RESUME_FOCUS_ITEMS[0].id}
        title="不同阶段，应突出哪些核心能力？"
        tip={abilityData['tip']}
      >
        <p className="mb-2">
          {`${displayName}的价值评估是一个动态过程，随经验增长，怎么写简历才不会显得要么太浅，要么过度包装？`}
        </p>
        <ExperienceSwitcher data={omit(abilityData, ['tip'])} />
      </GeneralChart>
      <GeneralChart
        id={RESUME_FOCUS_ITEMS[1].id}
        title="如何呈现你的工作成果？"
        tip={achievementData['tip']}
      >
        <p className="mb-2">
          从“能做事”到“能成事”的演化路径，随着经验增长，成果的呈现重点会不断上移，从技术执行到业务成效，再到组织与战略影响
        </p>
        <ExperienceSwitcher data={omit(achievementData, ['tip'])} />
        <Link
          className="block rounded-lg my-4 p-px text-sm [background:linear-gradient(180deg,#3F73D3_0%,#237EA3_95.62%)] group"
          href={RESUME_EVALUATION_ROUTER_PATH}
        >
          <div className="bg-white rounded-lg px-4 py-2 flex items-center">
            <SvgElectric className="shrink-0 mr-2.5" />
            <span className="group-hover:underline">
              你的简历足够突出成果吗？上传简历立即诊断
            </span>
          </div>
        </Link>
      </GeneralChart>
      <div className="rounded-lg px-10 py-5 my-8 flex justify-between items-center [background:linear-gradient(91.84deg,rgba(255,255,255,0.97)_22.58%,rgba(255,255,255,0.93)_87.2%),linear-gradient(0deg,#6792f0,#6792f0),#ffffff] border border-web-blue-500/[0.08] shadow-[0px_0px_20px_0px_#00000014]">
        <div>
          <p className="font-semibold text-xl mb-2">还没准备好简历？</p>
          <p className="text-xs">谈职专业简历编辑器，10分钟搞定！</p>
        </div>
        <Button
          className="!h-12 !w-40 !font-semibold"
          variant="contained"
          component={Link}
          href={
            isLogin
              ? CANDIDATES_ME_ROUTER_PATH
              : DEFAULT_CANDIDATES_ME_ROUTER_PATH
          }
        >
          立即创建
        </Button>
      </div>
      <GeneralChart
        id={RESUME_FOCUS_ITEMS[2].id}
        title="HR是如何筛选简历的？"
        tip={resumeScreeningData['tip']}
      >
        <p className="mb-2">{resumeScreeningData['总体说明'] || ''}</p>
        {Object.entries(omit(resumeScreeningData, ['总体说明', 'tip'])).map(
          ([key, value], index) => (
            <React.Fragment key={index}>
              <h4
                className={`font-semibold mb-2 ${index == 0 ? 'mt-0' : 'mt-4'}`}
              >
                {key}
              </h4>
              <p className="mb-2">{value?.['说明'] || ''}</p>
              <ul className="list-inside !ml-2">
                {(value?.['要点'] || []).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </React.Fragment>
          ),
        )}
      </GeneralChart>
      <GeneralChart
        id={RESUME_FOCUS_ITEMS[3].id}
        title="如何让你的简历脱颖而出？"
        tip={standoutData['tip']}
      >
        <p className="mb-2">
          了解 HR 的关注点后，你可以主动运用以下策略来构建一份极具针对性的简历。
        </p>
        {Object.entries(omit(standoutData, ['tip'])).map(
          ([key, value], index) => (
            <React.Fragment key={index}>
              <h4
                className={`font-semibold mb-2 ${index == 0 ? 'mt-0' : 'mt-4'}`}
              >
                {key}
              </h4>
              <p className="mb-2">{value?.['说明'] || ''}</p>
              <ul className="list-inside !ml-2 mb-2">
                {(value?.['建议要点'] || []).map(
                  (item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ),
                )}
              </ul>
              <p>{`示例表达：${value?.['示例表达'] || ''}`}</p>
            </React.Fragment>
          ),
        )}
      </GeneralChart>
      <GeneralChart
        id={RESUME_FOCUS_ITEMS[4].id}
        title="加分亮点让你脱颖而出"
        tip={highlightData['tip']}
      >
        <p className="mb-2">{highlightData['模块引言'] || ''}</p>
        {(highlightData['岗位专属亮点'] || []).map(
          (data: any, index: number) => (
            <React.Fragment key={index}>
              <h4
                className={`font-semibold mb-2 ${index == 0 ? 'mt-0' : 'mt-4'}`}
              >
                {data?.['亮点主题'] || ''}
              </h4>
              <p className="mb-2">{data?.['亮点说明'] || ''}</p>
              <ul className="list-inside !ml-2 mb-2">
                {(data?.['关键表现'] || []).map(
                  (item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ),
                )}
              </ul>
              <p>{`示例表达：${data?.['示例表达'] || ''}`}</p>
            </React.Fragment>
          ),
        )}
      </GeneralChart>
      <GeneralChart
        id={RESUME_FOCUS_ITEMS[5].id}
        title="市场偏爱的深层特质"
        tip={marketPreferenceData['tip']}
      >
        <p className="mb-2">{marketPreferenceData['模块引言'] || ''}</p>
        {(marketPreferenceData['市场偏爱的深层特质'] || []).map(
          (data: any, index: number) => (
            <React.Fragment key={index}>
              <h4
                className={`font-semibold mb-2 ${index == 0 ? 'mt-0' : 'mt-4'}`}
              >
                {data?.['特质名称'] || ''}
              </h4>
              <p className="mb-2">{data?.['特质说明'] || ''}</p>
              <ul className="list-inside !ml-2 mb-2">
                {(data?.['行为线索'] || []).map(
                  (item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ),
                )}
              </ul>
            </React.Fragment>
          ),
        )}
      </GeneralChart>
      <GeneralChart
        id={RESUME_FOCUS_ITEMS[6].id}
        title="必须规避的表述陷阱"
        tip={avoidTrapsData['tip']}
      >
        <p className="mb-2">{avoidTrapsData['模块引言'] || ''}</p>
        {(avoidTrapsData['表达陷阱'] || []).map((data: any, index: number) => (
          <React.Fragment key={index}>
            <h4
              className={`font-semibold mb-2 ${index == 0 ? 'mt-0' : 'mt-4'}`}
            >
              {data?.['陷阱名称'] || ''}
            </h4>
            <p className="mb-2">{data?.['问题说明'] || ''}</p>
            <ul className="list-inside !ml-2 mb-2">
              {(data?.['改进建议'] || []).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </React.Fragment>
        ))}
      </GeneralChart>
    </div>
  );
};
export default ResumeFocus;
